import {Component, Input, OnInit} from '@angular/core';
import {AbstractComponent, RegistrationDetailsModalComponent} from "@components";
import {
  ImportConfirmation, ListSortOrder,
  Lookups, PagedFilter, Pages, RegistrationFilter, RaceRegistration,
  StatementImport, StatementImportItem
} from "@model";
import {AccountStatementsService, LoaderService, TOAST_STATE, ToastService} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {ImportStatus} from "../../../../../model/statements/import-status";
import {BehaviorSubject, debounceTime, distinctUntilChanged} from "rxjs";
import {environment} from "@environment";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-account-statement-details-statements',
  templateUrl: './account-statement-details.component.html',
  styleUrls: ['./account-statement-details.component.scss']
})
export class AccountStatementDetailsComponent extends AbstractComponent implements OnInit {
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) accountStatements: StatementImport[] = [];
  @Input({required: true}) accountStatementItemList: StatementImportItem[] = [];
  @Input({required: true}) importId?: string;

  raceRegistrations: RaceRegistration[] = [];
  searchFilter: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  name: string = "";
  isLoading: boolean = false;
  accountStatement?: StatementImport;
  currentAccountStatementItemList: StatementImportItem[] = [];
  deletedAccountStatementItemList: StatementImportItem[] = [];

  constructor(private toastService: ToastService, private translocoService: TranslocoService,
              private accountStatementsService: AccountStatementsService, private loaderService: LoaderService,
              private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {
    this.currentAccountStatementItemList = this.accountStatementItemList;
    if (this.importId && (this.accountStatements?.length ?? 0) > 0) {
      this.accountStatement = this.accountStatements.find((item: StatementImport): boolean => item.id === (+this.importId!));
    }
    this.subscriptions.push(this.searchFilter.pipe(distinctUntilChanged(),
      debounceTime(environment.fullTextSearchDelay)).subscribe((value: any) => {
        this.isLoading = true;
        this.accountStatementsService.findMatchingRegistrations(new PagedFilter(new Pages(), new RegistrationFilter(value ?? ""),
          ListSortOrder.DESC)).subscribe((value: RaceRegistration[]) => {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
            this.raceRegistrations = value ?? [];
          }
        )
      })
    );
  }

  confirmImport(): void {
    let importConfirmation: ImportConfirmation = new ImportConfirmation(this.accountStatement!.id!,
      this.deletedAccountStatementItemList.map((item: StatementImportItem) => item.id!));
    this.loaderService.startLoading();
    this.accountStatementsService.confirmImport(importConfirmation, (statementImport: StatementImport) => {
      this.accountStatement = statementImport;
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success,
        this.translocoService.translate("account.statements.confirm.import.success"));
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("account.statements.confirm.import.error"));
    });
  }

  deleteStatementItem(statementImportItem: StatementImportItem): void {
    const index = this.currentAccountStatementItemList.indexOf(statementImportItem, 0);
    if (index > -1) {
      this.currentAccountStatementItemList.splice(index, 1);
    }
    this.deletedAccountStatementItemList.push(statementImportItem);
  }

  handleSearchInput(value: string | undefined): void {
    this.searchFilter.next(value);
  }

  registrationDetails(raceRegistration: RaceRegistration) {
    const modalRef: NgbModalRef = this.modalService.open(RegistrationDetailsModalComponent, {
      centered: true,
      windowClass: 'registration-details-modal',
    });
    modalRef.componentInstance.title = this.translocoService.translate("account.statements.registration.details");
    modalRef.componentInstance.raceRegistration = raceRegistration;
    modalRef.componentInstance.languages = this.languages;

    modalRef.result.then((statementImportItem:StatementImportItem) => {
      let items: StatementImportItem[]=[statementImportItem];
      this.loaderService.startLoading();
      this.accountStatementsService.connectImportAndRegistration(items, ()=>{
        this.loaderService.dismissLoading();
        let value: string | undefined = this.searchFilter.value;
        this.searchFilter.next(undefined);
        this.searchFilter.next(value);
        this.accountStatementsService.findImportItems(this.accountStatement!.id!).subscribe((items: StatementImportItem[])=>{
          this.accountStatementItemList = items;
          this.currentAccountStatementItemList = this.accountStatementItemList;
        });
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("account.statements.registration.connected.to.statement.item.success"));
      });
    }, (_) => {
      /// ignore
    });
  }

  protected readonly ImportStatus = ImportStatus;
}
